import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRefresh, 
  faPlay, 
  faCircleInfo,
  faTimes,
  faDownload,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import '../styles/JobMonitor.css';

const JOB_TYPE_OPTIONS = [
  { value: 'send-notification', label: 'Send Notification' },
  { value: 'execute-api-call', label: 'Execute API Call' },
  { value: 'generate-visit-summary', label: 'Generate Visit Summary' },
];

const JOB_TYPE_LABELS = {
  'send-notification': 'Send Notification',
  'execute-api-call': 'Execute API Call',
  'generate-visit-summary': 'Generate Visit Summary',
};

const JobMonitor = () => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    jobType: '',
    page: 1,
    limit: 20,
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [retryingJobId, setRetryingJobId] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [filters]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchJobs();
        fetchStats();
      }, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, filters]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/scheduling/jobs`,
        {
          params: filters,
        }
      );

      setJobs(response.data.jobs);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/scheduling/stats`
      );

      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const retryJob = async (jobId) => {
    try {
      setRetryingJobId(jobId);
      setError(null);
      await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/scheduling/job/${jobId}/retry`,
        {}
      );

      // Refresh jobs after retry
      await Promise.all([fetchJobs(), fetchStats()]);
      setSelectedJob((current) => (
        current && current.job_id === jobId
          ? { ...current, status: 'waiting', error_message: null }
          : current
      ));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to retry job');
    } finally {
      setRetryingJobId(null);
    }
  };

  const openJobDetails = (job) => {
    setSelectedJob(job);
  };

  const closeJobDetails = () => {
    setSelectedJob(null);
  };

  const getJobTypeLabel = (jobType) => JOB_TYPE_LABELS[jobType] || jobType;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FontAwesomeIcon icon={faCheckCircle} className="status-icon completed" />;
      case 'failed':
        return <FontAwesomeIcon icon={faTimesCircle} className="status-icon failed" />;
      case 'active':
        return <FontAwesomeIcon icon={faSpinner} className="status-icon active" spin />;
      case 'waiting':
        return <FontAwesomeIcon icon={faClock} className="status-icon waiting" />;
      default:
        return <FontAwesomeIcon icon={faClock} className="status-icon" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const exportJobs = () => {
    const csvContent = [
      ['Job ID', 'Type', 'Status', 'Created', 'Completed', 'Attempts', 'Error'].join(','),
      ...jobs.map(job => [
        job.job_id,
        job.job_type,
        job.status,
        formatDate(job.createdAt),
        formatDate(job.completed_at),
        job.attempts,
        job.error_message || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-executions-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="job-monitor">
      <div className="job-monitor-header">
        <h2>Job Monitor</h2>
        <div className="monitor-actions">
          <label className="auto-refresh">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto Refresh
          </label>
          <button onClick={fetchJobs} disabled={loading} className="btn-refresh">
            <FontAwesomeIcon icon={faRefresh} spin={loading} />
            Refresh
          </button>
          <button onClick={exportJobs} className="btn-export">
            <FontAwesomeIcon icon={faDownload} />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card waiting">
          <div className="stat-value">{stats.waiting || 0}</div>
          <div className="stat-label">Waiting</div>
        </div>
        <div className="stat-card active">
          <div className="stat-value">{stats.active || 0}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card completed">
          <div className="stat-value">{stats.completed || 0}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card failed">
          <div className="stat-value">{stats.failed || 0}</div>
          <div className="stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="job-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
          >
            <option value="">All</option>
            <option value="waiting">Waiting</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Type:</label>
          <select
            value={filters.jobType}
            onChange={(e) => setFilters({ ...filters, jobType: e.target.value, page: 1 })}
          >
            <option value="">All</option>
            {JOB_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Per Page:</label>
          <select
            value={filters.limit}
            onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value), page: 1 })}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Jobs Table */}
      <div className="jobs-table-container">
        <table className="jobs-table">
          <thead>
            <tr>
              <th>Job ID</th>
              <th>Type</th>
              <th>Status</th>
              <th>Created</th>
              <th>Scheduled</th>
              <th>Completed</th>
              <th>Attempts</th>
              <th>Creator</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && jobs.length === 0 ? (
              <tr>
                <td colSpan="9" className="loading">
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading jobs...
                </td>
              </tr>
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No jobs found
                </td>
              </tr>
            ) : (
              jobs.map((job) => (
                <tr key={job.id} className={`job-row ${job.status}`}>
                  <td>
                    <code>{job.job_id}</code>
                  </td>
                  <td>
                    <span className={`job-type ${job.job_type}`}>
                      {getJobTypeLabel(job.job_type)}
                    </span>
                  </td>
                  <td>
                    <div className="status-cell">
                      {getStatusIcon(job.status)}
                      <span className={`status-text ${job.status}`}>
                        {job.status}
                      </span>
                    </div>
                  </td>
                  <td>{formatDate(job.createdAt)}</td>
                  <td>{formatDate(job.scheduled_at)}</td>
                  <td>{formatDate(job.completed_at)}</td>
                  <td>
                    <span className="attempts">
                      {job.attempts}/{job.max_attempts}
                    </span>
                  </td>
                  <td>{job.Creator?.name || 'System'}</td>
                  <td>
                    <div className="job-actions">
                      <button
                        onClick={() => openJobDetails(job)}
                        className="btn-details"
                        title="View details"
                      >
                        <FontAwesomeIcon icon={faCircleInfo} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedJob && (
        <div className="job-details-backdrop" onClick={closeJobDetails}>
          <div className="job-details-modal" onClick={(event) => event.stopPropagation()}>
            <div className="job-details-header">
              <div>
                <h3>Job Details</h3>
                <p>{getJobTypeLabel(selectedJob.job_type)} #{selectedJob.job_id}</p>
              </div>
              <button className="btn-close-details" onClick={closeJobDetails} title="Close details">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="job-details-meta">
              <div><strong>Status:</strong> {selectedJob.status}</div>
              <div><strong>Attempts:</strong> {selectedJob.attempts}/{selectedJob.max_attempts}</div>
              <div><strong>Created:</strong> {formatDate(selectedJob.createdAt)}</div>
              <div><strong>Completed:</strong> {formatDate(selectedJob.completed_at)}</div>
            </div>

            {selectedJob.error_message && (
              <div className="job-details-error">
                <strong>Error</strong>
                <pre>{selectedJob.error_message}</pre>
              </div>
            )}

            <div className="job-details-section">
              <strong>Payload</strong>
              <pre>{JSON.stringify(selectedJob.payload || {}, null, 2)}</pre>
            </div>

            <div className="job-details-section">
              <strong>Result</strong>
              <pre>{JSON.stringify(selectedJob.result || {}, null, 2)}</pre>
            </div>

            <div className="job-details-actions">
              {selectedJob.status === 'failed' && (
                <button
                  onClick={() => retryJob(selectedJob.job_id)}
                  className="btn-retry"
                  disabled={retryingJobId === selectedJob.job_id}
                >
                  <FontAwesomeIcon icon={faPlay} />
                  {retryingJobId === selectedJob.job_id ? 'Retrying...' : 'Re-execute Job'}
                </button>
              )}
              <button onClick={closeJobDetails} className="btn-details-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobMonitor;
